import styled from "@emotion/styled";
import { map } from "lodash-es";

import { MDSIconKind, MDSText, MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds";
import { AgentLinkButton } from "@/pages/agent/components/agent-link-button";
import { AgentRowActionButton } from "@/pages/agent/components/agent-row-action-button";
import { AgentSectionHeader } from "@/pages/agent/components/agent-section-header";
import { AgentTaskStateIcon } from "@/pages/agent/components/agent-task-state-icon";
import { formatAgentDate } from "@/pages/agent/utils/agent-date.utils";
import { TaskRowViewModel, TaskSectionKind } from "@/pages/agent/tasks/agent-tasks-page.utils";

export const AgentTasksSection = ({
  title,
  kind,
  taskRows,
  onOpenProject,
  onOpenTask,
  onToggleTaskComplete,
  onCommentTask,
}: {
  title: string;
  kind: TaskSectionKind;
  taskRows: TaskRowViewModel[];
  onOpenProject: ({ projectId }: { projectId: string }) => void;
  onOpenTask: ({ taskId }: { taskId: string }) => void;
  onToggleTaskComplete: ({ taskId }: { taskId: string }) => void;
  onCommentTask: ({ taskId }: { taskId: string }) => void;
}) => (
  <StyledSectionRoot>
    <AgentSectionHeader title={title} />

    <StyledTaskTable role="table">
      <StyledTaskHeader role="row">
        <StyledHeaderCell role="columnheader">Description</StyledHeaderCell>
        <StyledHeaderCell role="columnheader">Project</StyledHeaderCell>
        <StyledHeaderCell role="columnheader">{getFollowUpHeaderLabel({ kind })}</StyledHeaderCell>
        <StyledHeaderCell role="columnheader">{getTaskDateHeaderLabel({ kind })}</StyledHeaderCell>
        <StyledHeaderCell role="columnheader" />
      </StyledTaskHeader>

      {taskRows.length === 0 ? (
        <StyledEmptyState>
          <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
            {getEmptyStateLabel({ kind })}
          </MDSText>
        </StyledEmptyState>
      ) : (
        map(taskRows, taskRow => {
          const project = taskRow.project;
          const followUpDisplayDate = getFollowUpDisplayDate({ kind, taskRow });
          const taskDisplayDate = getTaskDisplayDate({ kind, taskRow });

          const taskTitle = taskRow.task.title || "Untitled task";
          const toggleAriaLabel =
            kind === TaskSectionKind.Completed
              ? `Mark ${taskTitle} incomplete`
              : `Mark ${taskTitle} complete`;

          return (
            <StyledTaskRow key={taskRow.task.id} role="row">
              <StyledDescriptionCell role="cell">
                <StyledTaskStateButton
                  type="button"
                  aria-label={toggleAriaLabel}
                  onClick={() => {
                    onToggleTaskComplete({ taskId: taskRow.task.id });
                  }}
                >
                  <AgentTaskStateIcon completed={kind === TaskSectionKind.Completed} />
                </StyledTaskStateButton>
                <StyledTaskTitleButton
                  type="button"
                  onClick={() => {
                    onOpenTask({ taskId: taskRow.task.id });
                  }}
                >
                  <MDSText
                    color={MDSTextColor.Primary}
                    variant={MDSTextVariant.TextS}
                    weight={MDSTextWeight.SemiBold}
                  >
                    {taskTitle}
                  </MDSText>
                </StyledTaskTitleButton>
              </StyledDescriptionCell>

              <StyledProjectCell role="cell">
                {project ? (
                  <AgentLinkButton
                    truncate
                    onClick={() => {
                      onOpenProject({ projectId: project.id });
                    }}
                  >
                    {taskRow.projectTitle}
                  </AgentLinkButton>
                ) : (
                  <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextS}>
                    {taskRow.projectTitle}
                  </MDSText>
                )}
              </StyledProjectCell>

              <StyledTaskCell role="cell">
                <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
                  {followUpDisplayDate}
                </MDSText>
              </StyledTaskCell>

              <StyledTaskCell role="cell">
                <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
                  {taskDisplayDate}
                </MDSText>
              </StyledTaskCell>

              <StyledActionCell role="cell">
                <AgentRowActionButton
                  aria-label={`Comment on ${taskTitle}`}
                  iconKind={MDSIconKind.Message}
                  onClick={() => {
                    onCommentTask({ taskId: taskRow.task.id });
                  }}
                />
              </StyledActionCell>
            </StyledTaskRow>
          );
        })
      )}
    </StyledTaskTable>
  </StyledSectionRoot>
);

const getFollowUpHeaderLabel = ({ kind }: { kind: TaskSectionKind }) => {
  if (kind === TaskSectionKind.Open) {
    return "Next Follow-up";
  }

  return "Last Follow-up";
};

const getTaskDateHeaderLabel = ({ kind }: { kind: TaskSectionKind }) => {
  if (kind === TaskSectionKind.Open) {
    return "Due Date";
  }

  return "Completed Date";
};

const getEmptyStateLabel = ({ kind }: { kind: TaskSectionKind }) => {
  if (kind === TaskSectionKind.Open) {
    return "No open tasks";
  }

  return "No completed tasks";
};

const getFollowUpDisplayDate = ({
  kind,
  taskRow,
}: {
  kind: TaskSectionKind;
  taskRow: TaskRowViewModel;
}) => {
  if (kind === TaskSectionKind.Open) {
    return formatAgentDate({
      value: taskRow.nextFollowUp?.scheduledFor ?? taskRow.nextFollowUp?.dueAt,
    });
  }

  return formatAgentDate({
    value:
      taskRow.lastFollowUp?.completedAt ??
      taskRow.lastFollowUp?.firedAt ??
      taskRow.lastFollowUp?.scheduledFor,
    includeTime: true,
  });
};

const getTaskDisplayDate = ({
  kind,
  taskRow,
}: {
  kind: TaskSectionKind;
  taskRow: TaskRowViewModel;
}) => {
  if (kind === TaskSectionKind.Open) {
    return formatAgentDate({ value: taskRow.task.dueAt, includeTime: true });
  }

  return formatAgentDate({
    value: taskRow.task.updatedAt,
    includeTime: true,
  });
};

const StyledSectionRoot = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const StyledTaskTable = styled.div`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: 12px;
  overflow-x: auto;
`;

const taskTableColumns =
  "minmax(280px, 1fr) minmax(140px, 0.38fr) minmax(132px, 0.34fr) minmax(132px, 0.34fr) 36px";

const StyledTaskHeader = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.surface};
  display: grid;
  gap: ${({ theme }) => theme.spacing[3]};
  grid-template-columns: ${taskTableColumns};
  min-width: 760px;
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[5]};
`;

const StyledHeaderCell = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[MDSTextVariant.TextXs]};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  min-width: 0;
`;

const StyledTaskRow = styled.div`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.surface};
  display: grid;
  gap: ${({ theme }) => theme.spacing[3]};
  grid-template-columns: ${taskTableColumns};
  min-width: 760px;
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[5]};

  &:hover {
    background: ${({ theme }) => theme.colors.background.input.chrome.hover};
  }

  &:last-child {
    border-bottom: 0;
  }
`;

const StyledDescriptionCell = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  min-width: 0;
`;

const StyledTaskStateButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 24px;
  justify-content: center;
  padding: 0;
  width: 24px;

  &:hover {
    background: ${({ theme }) => theme.colors.background.menu.selected};
  }
`;

const StyledTaskTitleButton = styled.button`
  background: transparent;
  border: 0;
  cursor: pointer;
  min-width: 0;
  padding: 0;
  text-align: left;

  span {
    overflow-wrap: anywhere;
  }

  &:hover span {
    color: ${({ theme }) => theme.colors.text.emphasis};
  }
`;

const StyledProjectCell = styled.div`
  min-width: 0;
`;

const StyledTaskCell = styled.div`
  min-width: 0;

  span {
    overflow-wrap: anywhere;
  }
`;

const StyledActionCell = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
`;

const StyledEmptyState = styled.div`
  min-width: 760px;
  padding: ${({ theme }) => theme.spacing[6]} ${({ theme }) => theme.spacing[5]};
`;
